type SkeletonProps = {
  className?: string
}

/* A shimmer placeholder. The animation is CSS rather than GSAP because it
   loops forever and must not hold a ScrollTrigger or a timeline open. */
export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={`animate-shimmer rounded-lg bg-[linear-gradient(100deg,var(--color-line-soft)_38%,var(--color-surface)_50%,var(--color-line-soft)_62%)] bg-[length:220%_100%] ${className}`}
    />
  )
}

/* The vehicle card's own loading shape, so the grid does not jump when the
   real cards arrive. Mirrors VehicleCard's frame — the same 24px shell, the
   full-bleed media at its top, and a block for each of its bands — so the
   swap lands the card's parts where the placeholder already had them. */
export function VehicleCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-line bg-raised shadow-card">
      <Skeleton className="aspect-16/10 w-full rounded-none" />

      <div className="px-5 pt-4.5 pb-5">
        <Skeleton className="h-5.5 w-3/5" />
        <Skeleton className="mt-2.5 h-3 w-2/5" />

        {/* The hairline-divided 2×2 spec plate. */}
        <Skeleton className="mt-4 h-19 w-full rounded-2xl" />

        {/* The assurance pills, which wrap rather than sit on a grid. */}
        <div className="mt-4 flex flex-wrap gap-1.5">
          <Skeleton className="h-6 w-24 rounded-full" />
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-28 rounded-full" />
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>

        {/* The money line above its hairline, then the pill and the round
            WhatsApp mark. */}
        <div className="mt-4 flex items-end justify-between border-t border-line-soft pt-4">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-7 w-14" />
        </div>

        <div className="mt-4 grid grid-cols-[1fr_auto] gap-2">
          <Skeleton className="h-12 rounded-full" />
          <Skeleton className="size-12 rounded-full" />
        </div>
      </div>
    </div>
  )
}

/* The inventory grid's shape while the floor loads. Shorter than the card
   above it because the card it stands in for is: a meta strip, an inset
   picture, a name and a figure. */
export function InventoryCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-raised shadow-card">
      <div className="flex items-center justify-between px-3.5 pt-3">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="size-5 rounded-full" />
      </div>

      <Skeleton className="mx-3 mt-2.5 aspect-16/10 w-[calc(100%-1.5rem)] rounded-xl" />

      <div className="px-4 pt-3.5 pb-4">
        <Skeleton className="h-4.5 w-2/5" />
        <div className="mt-2 flex items-end justify-between gap-3">
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
    </div>
  )
}

export default Skeleton
